
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import attendanceService from '../../services/AttendanceService';
import { fetchAllEmployees } from './employeeSlice';

// Constants
const MAP_STATUS = {
  'P': 'Pending',
  'A': 'Approved',
  'C': 'Cancelled',
  'R': 'Rejected',
};

const ADJ_TYPE_MAP = {
  'II': 'Incorrect IN Time',
  'IO': 'Incorrect OUT Time',
  'IB': 'Incorrect IN and OUT Times',
  'IIO': 'Incorrect IN and OUT Times', // legacy code
  'MI': 'Missing IN Time',
  'MO': 'Missing OUT Time',
  'MF': 'Missing Full Day',
  'WS': 'Wrong Status',
};

// Helper functions
function getSafeValue(obj, path, defaultValue = '') {
  if (!obj) return defaultValue;
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    if (value && value[key] !== undefined && value[key] !== null) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }
  return value;
}

function normalizeDate(dateValue) {
  if (!dateValue) return '';
  let value = String(dateValue).trim();
  if (value.includes('T')) {
    value = value.split('T')[0];
  } else if (value.includes(' ')) {
    value = value.split(' ')[0];
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const slashMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return value;
}

function formatDateForDisplay(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatTimeWithAMPM(timeStr) {
  if (
    timeStr === null || timeStr === undefined || timeStr === '' ||
    timeStr === '—' || timeStr === 'null' || timeStr === 'undefined'
  ) {
    return '—';
  }
  try {
    const value = String(timeStr).trim();

    // Handle AM/PM format
    const ampmAnywhereMatch = value.match(/(\d{1,2})[:.](\d{2})(?::(\d{2}))?\s*(AM|PM)/i);
    if (ampmAnywhereMatch) {
      let hours = parseInt(ampmAnywhereMatch[1], 10);
      const minutes = ampmAnywhereMatch[2];
      const ampm = ampmAnywhereMatch[4].toUpperCase();
      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

    // Handle 24-hour formats
    const timeMatch = value.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1], 10);
      const minutes = timeMatch[2];
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

    return value;
  } catch {
    return String(timeStr);
  }
}

export const fetchAdjustmentRequests = createAsyncThunk(
  'attendanceApproval/fetchRequests',
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      // Ensure employee master data is loaded so we can resolve
      // service no / name / department from the employee ID.
      let employeesState = getState().employees;
      if (!employeesState?.employees || employeesState.employees.length === 0) {
        await dispatch(fetchAllEmployees());
        employeesState = getState().employees;
      }
      const employeeList = employeesState?.employees || [];

      // Build a lookup map: employeeId -> employee record
      const employeeMap = new Map();
      employeeList.forEach((emp) => {
        const id = String(emp.HED_EMPLOYEE_ID || '');
        if (id) employeeMap.set(id, emp);
      });

      const response = await attendanceService.GetAttendanceAdjList(null);

      if (response && response.ResultSet) {
        let data = response.ResultSet;

        // Normalize data to array
        if (!Array.isArray(data)) {
          if (data.data && Array.isArray(data.data)) {
            data = data.data;
          } else if (data.items && Array.isArray(data.items)) {
            data = data.items;
          } else if (data.list && Array.isArray(data.list)) {
            data = data.list;
          } else {
            data = Object.values(data).filter(Array.isArray).flat() || [];
          }
        }

        if (data && data.length > 0) {
          const mappedRequests = data.map((item, index) => {
            // Raw employee ID from the adjustment record itself
            const employeeId = String(
              getSafeValue(item, 'HAA_EMPLOYEE_ID') ||
              getSafeValue(item, 'EmployeeId') ||
              getSafeValue(item, 'employeeId') || ''
            );

            // Resolve the real employee record from the employee master list
            const emp = employeeMap.get(employeeId);

            const staffId = emp?.HED_SERVICE_NO || employeeId || '';

            const adjTypeCode = getSafeValue(item, 'HAA_ADJ_TYPE') ||
              getSafeValue(item, 'AdjType') ||
              getSafeValue(item, 'adjType') || '';

            const statusCode = getSafeValue(item, 'HAA_ADJ_STATUS') ||
              getSafeValue(item, 'HAA_STATUS') ||
              getSafeValue(item, 'AdjStatus') ||
              getSafeValue(item, 'adjStatus') || 'P';

            const attendanceDate = getSafeValue(item, 'HAA_ATTENDANCE_DATE') ||
              getSafeValue(item, 'attendanceDate') || '';

            const requestedInTime = getSafeValue(item, 'HAA_IN_TIME') ||
              getSafeValue(item, 'InTime') ||
              getSafeValue(item, 'inTime') || '';

            const requestedOutTime = getSafeValue(item, 'HAA_OUT_TIME') ||
              getSafeValue(item, 'OutTime') ||
              getSafeValue(item, 'outTime') || '';

            const adjustmentId = getSafeValue(item, 'HAA_ADJUSTMENTS_ID') ||
              getSafeValue(item, 'HAA_ADJ_ID') ||
              getSafeValue(item, 'adjustmentId') ||
              getSafeValue(item, 'AdjId') ||
              getSafeValue(item, 'id') ||
              `temp-${index}`;

            // Department: prefer the employee master record
            const department = emp?.HDD_DEPARTMENT_NAME ||
              getSafeValue(item, 'HAA_DEPARTMENT') ||
              getSafeValue(item, 'Department') ||
              getSafeValue(item, 'department') ||
              getSafeValue(item, 'HAA_DEPT_NAME') ||
              getSafeValue(item, 'DeptName') ||
              getSafeValue(item, 'deptName') ||
              '';

            // Employee name: prefer the employee master record
            const employeeName = emp?.HED_REPORT_NAME ||
              (emp ? `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim() : '') ||
              getSafeValue(item, 'HAA_EMPLOYEE_NAME') ||
              getSafeValue(item, 'EmployeeName') ||
              getSafeValue(item, 'employeeName') ||
              getSafeValue(item, 'Name') ||
              getSafeValue(item, 'name') ||
              (staffId ? `Employee ${staffId}` : 'Unknown Employee');

            // Designation: prefer the employee master record
            const designation = emp?.HED_DESIGNATION_NAME ||
              getSafeValue(item, 'HAA_DESIGNATION') ||
              getSafeValue(item, 'Designation') ||
              getSafeValue(item, 'designation') ||
              getSafeValue(item, 'HED_DESIGNATION') ||
              '';

            return {
              id: adjustmentId,
              name: employeeName,
              department: department,
              staffId,
              employeeId,
              designation: designation,
              date: formatDateForDisplay(attendanceDate),
              attendanceDate: normalizeDate(attendanceDate),
              attendanceDateRaw: attendanceDate,
              type: ADJ_TYPE_MAP[adjTypeCode] || adjTypeCode,
              adjTypeCode,
              requestedInTime: formatTimeWithAMPM(requestedInTime),
              requestedOutTime: formatTimeWithAMPM(requestedOutTime),
              requested: formatRequestedTime(adjTypeCode, requestedInTime, requestedOutTime),
              reason: getSafeValue(item, 'HAA_REASON') ||
                getSafeValue(item, 'HAA_ADJ_REASON') ||
                getSafeValue(item, 'AdjReason') ||
                getSafeValue(item, 'reason') || '—',
              status: MAP_STATUS[statusCode] || 'Pending',
              original: '—',
              originalInTime: '—',
              originalOutTime: '—',
              raw: {
                adjustmentId,
                employeeId,
                adjStatus: statusCode,
                inTime: requestedInTime,
                outTime: requestedOutTime,
                attendanceDate: normalizeDate(attendanceDate),
                attendanceDateRaw: attendanceDate
              }
            };
          });

          return mappedRequests;
        }
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load adjustment requests');
    }
  }
);

export const fetchOriginalAttendance = createAsyncThunk(
  'attendanceApproval/fetchOriginalAttendance',
  async ({ requests }, { rejectWithValue }) => {
    try {
      const enrichedRequests = await Promise.all(
        requests.map(async (req) => {
          if (!req.staffId || !req.attendanceDate) {
            return req;
          }

          try {
            const dateObj = new Date(req.attendanceDate);
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');

            const attResp = await attendanceService.GetFilteredAttendanceDetails({
              year: year,
              month: month,
              sno: req.staffId,
            });

            let attList = [];
            if (attResp?.ResultSet && Array.isArray(attResp.ResultSet)) {
              attList = attResp.ResultSet;
            } else if (Array.isArray(attResp)) {
              attList = attResp;
            }

            // Find matching attendance record
            let attRecord = attList.find(
              (a) => {
                const aSno = String(a.ServiceNo || a.sno || a.employeeId || a.EmployeeId || '');
                const aDate = normalizeDate(a.AttDate || a.attDate || a.attendanceDate || a.AttendanceDate || '');
                return aSno === String(req.staffId) && aDate === req.attendanceDate;
              }
            );

            if (!attRecord && attList.length > 0) {
              attRecord = attList.find(
                (a) => normalizeDate(a.AttDate || a.attDate || a.attendanceDate || a.AttendanceDate || '') === req.attendanceDate
              );
            }

            if (!attRecord && attList.length > 0) {
              attRecord = attList[0];
            }

            if (!attRecord) {
              return req;
            }

            const originalInTime = formatTimeWithAMPM(attRecord.InTime || attRecord.inTime);
            const originalOutTime = formatTimeWithAMPM(attRecord.OutTime || attRecord.outTime);

            let original = '—';

            if (req.adjTypeCode === 'IB' || req.adjTypeCode === 'IIO') {
              original = `${originalInTime} / ${originalOutTime}`;
            } else {
              switch (req.adjTypeCode) {
                case 'II':
                case 'MI':
                  original = originalInTime;
                  break;
                case 'IO':
                case 'MO':
                  original = originalOutTime;
                  break;
                case 'MF':
                  original = `${originalInTime} / ${originalOutTime}`;
                  break;
                default:
                  if (originalInTime !== '—' && originalOutTime !== '—') {
                    original = `${originalInTime} / ${originalOutTime}`;
                  } else if (originalInTime !== '—') {
                    original = originalInTime;
                  } else if (originalOutTime !== '—') {
                    original = originalOutTime;
                  }
                  break;
              }
            }
            const departmentFromAtt = getSafeValue(attRecord, 'Department') ||
              getSafeValue(attRecord, 'department') ||
              getSafeValue(attRecord, 'HDD_DEPARTMENT_NAME') ||
              getSafeValue(attRecord, 'DeptName');

            return {
              ...req,
              name: (req.name && !req.name.startsWith('Employee ') ? req.name : null) ||
                attRecord.Name || 
                attRecord.EmployeeName ||
                req.name,
              department: req.department || departmentFromAtt || '',
              original,
              originalInTime,
              originalOutTime,
            };
          } catch (error) {
            console.error(`Failed to fetch original attendance for ${req.staffId}:`, error);
            return req;
          }
        })
        
      );

      return enrichedRequests;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch original attendance');
    }
    
  }
);

export const approveAdjustment = createAsyncThunk(
  'attendanceApproval/approve',
  async ({ adjustmentId, userId }, { rejectWithValue }) => {
    try {
      const response = await attendanceService.PostApprovedAdj({
        adjustmentId,
        userId,
      });
      if (response && response.StatusCode === 200) {
        return { adjustmentId, status: 'Approved' };
      } else {
        throw new Error(response?.Result || 'Approval failed');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to approve adjustment');
    }
  }
);

export const rejectAdjustment = createAsyncThunk(
  'attendanceApproval/reject',
  async ({ adjustmentId, userId }, { rejectWithValue }) => {
    try {
      const response = await attendanceService.CancelAdj({
        adjustmentId,
        userId,
      });
      if (response && response.StatusCode === 200) {
        return { adjustmentId, status: 'Cancelled' };
      } else {
        throw new Error(response?.Result || 'Rejection failed');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reject adjustment');
    }
  }
);

// Helper function
function formatRequestedTime(adjTypeCode, inTime, outTime) {
  const formattedIn = formatTimeWithAMPM(inTime);
  const formattedOut = formatTimeWithAMPM(outTime);

  switch (adjTypeCode) {
    case 'II':
    case 'MI':
      return formattedIn;
    case 'IO':
    case 'MO':
      return formattedOut;
    case 'MF':
      return `${formattedIn} / ${formattedOut}`;
    case 'IB':
    case 'IIO':
      // Remove "IN" and "OUT" labels
      return `${formattedIn} / ${formattedOut}`;
    default:
      if (formattedIn !== '—' && formattedOut !== '—') {
        return `${formattedIn} / ${formattedOut}`;
      } else if (formattedIn !== '—') {
        return formattedIn;
      } else if (formattedOut !== '—') {
        return formattedOut;
      }
      return '—';
  }
}

// Initial State
const initialState = {
  requests: [],
  loading: false,
  error: null,
  // Filter state
  activeTab: 'Pending',
  search: '',
  department: 'All departments',
  date: '',
  page: 1,
  actionLoading: null,
  cache: {},
};

// Slice
const attendanceApprovalSlice = createSlice({
  name: 'attendanceApproval',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      state.page = 1;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 1;
    },
    setDepartment: (state, action) => {
      state.department = action.payload;
      state.page = 1;
    },
    setDate: (state, action) => {
      state.date = action.payload;
      state.page = 1;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdjustmentRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdjustmentRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
        state.error = null;
      })
      .addCase(fetchAdjustmentRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load adjustment requests';
        state.requests = [];
      })

      // Fetch Original Attendance
      .addCase(fetchOriginalAttendance.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOriginalAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(fetchOriginalAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch original attendance';
      })

      // Approve
      .addCase(approveAdjustment.pending, (state, action) => {
        state.actionLoading = action.meta.arg.adjustmentId;
        state.error = null;
      })
      .addCase(approveAdjustment.fulfilled, (state, action) => {
        state.actionLoading = null;
        const { adjustmentId, status } = action.payload;
        const request = state.requests.find(r => r.id === adjustmentId);
        if (request) {
          request.status = status;
          request.raw.adjStatus = 'A';
        }
      })
      .addCase(approveAdjustment.rejected, (state, action) => {
        state.actionLoading = null;
        state.error = action.payload || 'Failed to approve adjustment';
      })

      // Reject
      .addCase(rejectAdjustment.pending, (state, action) => {
        state.actionLoading = action.meta.arg.adjustmentId;
        state.error = null;
      })
      .addCase(rejectAdjustment.fulfilled, (state, action) => {
        state.actionLoading = null;
        const { adjustmentId, status } = action.payload;
        const request = state.requests.find(r => r.id === adjustmentId);
        if (request) {
          request.status = status;
          request.raw.adjStatus = 'C';
        }
      })
      .addCase(rejectAdjustment.rejected, (state, action) => {
        state.actionLoading = null;
        state.error = action.payload || 'Failed to reject adjustment';
      });
  },
});

// Export actions
export const {
  setActiveTab,
  setSearch,
  setDepartment,
  setDate,
  setPage,
  clearError,
  resetState,
} = attendanceApprovalSlice.actions;

// Selectors
export const selectApprovalState = (state) => state.attendanceApproval;
export const selectRequests = (state) => state.attendanceApproval.requests;
export const selectLoading = (state) => state.attendanceApproval.loading;
export const selectError = (state) => state.attendanceApproval.error;
export const selectActiveTab = (state) => state.attendanceApproval.activeTab;
export const selectSearch = (state) => state.attendanceApproval.search;
export const selectDepartment = (state) => state.attendanceApproval.department;
export const selectDate = (state) => state.attendanceApproval.date;
export const selectPage = (state) => state.attendanceApproval.page;
export const selectActionLoading = (state) => state.attendanceApproval.actionLoading;

export default attendanceApprovalSlice.reducer;